/**
 * The iOS install banner's markup, and nothing else.
 *
 * Two consumers, one box:
 *   - IosInstallPrompt renders it with the real copy, link and handlers;
 *   - the prerender's StaticFrame renders it INVISIBLE and empty (no copy, no
 *     link, no icon request) as a placeholder that an inline script reveals
 *     on an eligible iPhone, so the banner mounting above the page shifts
 *     nothing.
 * Its height is the point, so both must render this component rather than a
 * look-alike, and its text rows are single lines (truncated) so that height
 * is the same at every width and in every language.
 *
 * Pure: no hooks, no browser globals (the prerender renders it in Node).
 */
export default function IosInstallBanner({
  title,
  body,
  getLabel,
  dismissLabel,
  href,
  iconSrc,
  onGet,
  onDismiss,
}: {
  title: string
  body: string
  getLabel: string
  dismissLabel: string
  href?: string
  iconSrc?: string
  onGet?: () => void
  onDismiss?: () => void
}) {
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 bg-gray-900/95 border-b border-gray-800">
      <img src={iconSrc} alt="" className="w-8 h-8 rounded-lg shrink-0" />
      {/* One line each, always (truncate): the banner's height must not
          depend on the viewport or the language, or the prerendered slot,
          which holds one blank line per row, would not match it. Measured
          in Chrome 2026-10-01: the old copy ("Commander Bracket for
          iPhone") wrapped below 390px and grew the banner from 53.5px to
          71px (360-375) and 86px (320). The copy is now short enough to
          fit at every width: at 320px the text column is 150px and the
          rows need 99px ("Now on iPhone") and 115px ("Free, native, no
          ads."), so nothing is cut and the banner is 53.5px from 320 to
          430. truncate stays as the safety net for longer copy. */}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-100 font-medium leading-tight truncate">{title}</p>
        <p className="text-xs text-gray-400 leading-tight truncate">{body}</p>
      </div>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onGet}
        className="shrink-0 px-3 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-sm font-medium transition-colors"
      >
        {getLabel}
      </a>
      <button
        type="button"
        aria-label={dismissLabel}
        onClick={onDismiss}
        className="shrink-0 px-1.5 text-gray-500 hover:text-gray-300 text-lg leading-none"
      >
        &times;
      </button>
    </div>
  )
}
